import { useEffect } from 'react';
import { useUserActivityTracking } from './useUserActivityTracking';

/**
 * Hook to integrate user activity tracking throughout the app
 */
export const useActivityIntegration = () => {
  const { 
    trackClick, 
    trackFormInteraction, 
    trackSearch, 
    trackContentInteraction, 
    trackFeatureUsage,
    trackError 
  } = useUserActivityTracking();

  // Add global click tracking
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target) return;

      // Track button clicks
      if (target.tagName === 'BUTTON' || target.closest('button')) {
        const button = target.tagName === 'BUTTON' ? target : target.closest('button');
        const buttonText = button?.textContent?.trim() || 'unknown';
        const buttonId = button?.id || button?.className || 'anonymous-button';
        
        trackClick(buttonId, 'button', {
          text: buttonText,
          tagName: target.tagName,
          className: target.className
        });
      }

      // Track link clicks
      if (target.tagName === 'A' || target.closest('a')) {
        const link = target.tagName === 'A' ? target : target.closest('a');
        const href = (link as HTMLAnchorElement)?.href;
        const linkText = link?.textContent?.trim() || 'unknown';
        
        trackClick(href || 'unknown-link', 'link', {
          text: linkText,
          href: href,
          external: href && !href.includes(window.location.origin)
        });
      }

      // Track card/content clicks
      if (target.closest('[data-content-id]')) {
        const contentElement = target.closest('[data-content-id]');
        const contentId = contentElement?.getAttribute('data-content-id') || 'unknown';
        const contentType = contentElement?.getAttribute('data-content-type') || 'unknown';
        
        trackContentInteraction(contentId, contentType, 'click');
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, [trackClick, trackContentInteraction]);

  // Add global form tracking
  useEffect(() => {
    const handleFormSubmit = (event: Event) => {
      const form = event.target as HTMLFormElement;
      if (form?.tagName === 'FORM') {
        const formId = form.id || form.className || 'anonymous-form';
        trackFormInteraction(formId, 'submit', {
          action: form.action,
          method: form.method
        });
      }
    };

    const handleFormFocus = (event: Event) => {
      const target = event.target as HTMLElement;
      const form = target.closest('form');
      if (form && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        const formId = form.id || form.className || 'anonymous-form';
        trackFormInteraction(formId, 'start', {
          firstField: target.tagName
        });
      }
    };

    document.addEventListener('submit', handleFormSubmit);
    document.addEventListener('focusin', handleFormFocus);
    
    return () => {
      document.removeEventListener('submit', handleFormSubmit);
      document.removeEventListener('focusin', handleFormFocus);
    };
  }, [trackFormInteraction]);

  // Add global error tracking
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      trackError('javascript', event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      trackError('promise_rejection', String(event.reason), {
        reason: event.reason
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [trackError]);

  return {
    trackClick,
    trackFormInteraction,
    trackSearch,
    trackContentInteraction,
    trackFeatureUsage,
    trackError
  };
};